import { create } from 'zustand';

/** What the API reported in a 403 APPROVAL_REQUIRED: exactly what the approval must be bound to. */
export type ApprovalNeed = {
  action: string;
  branchId: string;
  targetId: string | null;
  amountMinor: number;
};

export type Prompt = {
  id: number;
  need: ApprovalNeed;
  /** the reason the person already typed in the action's own dialog: it prefills the note */
  reason: string | undefined;
  settle: (token: string | null) => void;
};

type PromptState = { current: Prompt | null; queue: Prompt[] };

/** The counter dialog (`ApprovalHost`) shows `current`; a second guarded call waits its turn. */
export const usePrompt = create<PromptState>(() => ({ current: null, queue: [] }));

let promptCount = 0;

/** Resolves the approval token, or null when the person gives up (closes the dialog). */
// executor form on purpose: tsconfig lib is ES2023 and counter iPads may run Safari < 17.4 (no Promise.withResolvers)
export function askApproval(need: ApprovalNeed, reason?: string): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false;
    const prompt: Prompt = {
      id: ++promptCount,
      need,
      reason,
      settle: (token) => {
        if (done) return;
        done = true;
        resolve(token);
        const [next, ...rest] = usePrompt.getState().queue;
        usePrompt.setState({ current: next ?? null, queue: rest });
      },
    };
    const { current, queue } = usePrompt.getState();
    if (current) usePrompt.setState({ queue: [...queue, prompt] });
    else usePrompt.setState({ current: prompt });
  });
}

const isNeed = (d: unknown): d is ApprovalNeed =>
  typeof d === 'object' &&
  d !== null &&
  'action' in d &&
  typeof d.action === 'string' &&
  'branchId' in d &&
  typeof d.branchId === 'string' &&
  'amountMinor' in d &&
  typeof d.amountMinor === 'number' &&
  'targetId' in d &&
  (d.targetId === null || typeof d.targetId === 'string');

/** The details of a 403 APPROVAL_REQUIRED answer, else null. Reads a clone: the caller still gets the response. */
async function approvalNeedOf(response: Response): Promise<ApprovalNeed | null> {
  if (response.status !== 403) return null;
  try {
    const body: unknown = await response.clone().json();
    if (typeof body !== 'object' || body === null || !('error' in body)) return null;
    const { error } = body;
    if (typeof error !== 'object' || error === null) return null;
    if (!('code' in error) || error.code !== 'APPROVAL_REQUIRED' || !('details' in error)) return null;
    return isNeed(error.details) ? error.details : null;
  } catch {
    return null;
  }
}

/** The `reason` field of a JSON request body (void / refund dialogs already asked for it), else undefined. */
async function reasonOf(request: Request): Promise<string | undefined> {
  try {
    const body: unknown = await request.json();
    if (typeof body !== 'object' || body === null || !('reason' in body)) return undefined;
    const text = typeof body.reason === 'string' ? body.reason.trim() : '';
    return text.length >= 3 ? text.slice(0, 250) : undefined;
  } catch {
    return undefined;
  }
}
/** A request with two guarded actions (lower price + discount) needs one approval each. */
const MAX_APPROVALS_PER_CALL = 4;

/**
 * Wraps `fetch`: a 403 APPROVAL_REQUIRED on any call opens the manager-approval dialog; once a manager approves, the
 * same request is sent again with the token(s) in `X-Approval-Token` (comma separated). The caller just sees a slower
 * call. When the person gives up (or a manager says no) it gets the original 403, so the usual error path runs.
 */
export function withApproval(send: typeof fetch, ask: typeof askApproval = askApproval): typeof fetch {
  return async (input, init) => {
    const request = new Request(input, init);
    if (request.method === 'GET' || request.method === 'HEAD') return send(request);
    const spare = request.clone(); // a body can be sent once; the retry needs its own copy
    let response = await send(request);
    const tokens: string[] = [];
    let reason: string | undefined;
    while (tokens.length < MAX_APPROVALS_PER_CALL) {
      const need = await approvalNeedOf(response);
      if (!need) break;
      reason ??= await reasonOf(spare.clone());
      const token = await ask(need, reason);
      if (!token) break;
      tokens.push(token);
      const headers = new Headers(spare.headers);
      headers.set('X-Approval-Token', tokens.join(','));
      response = await send(new Request(spare.clone(), { headers }));
    }
    return response;
  };
}
